/**
 * Pure helpers for fetching and reconstructing a session's committed
 * conversation from the backend checkpoint (GET /sessions/{id}/history).
 *
 * Kept free of React imports so it can be unit tested in isolation and
 * reused by {@link ChatSocketProvider}.
 */
import {
  ChatHistoryMessage,
  Message,
  SessionHistoryResponse,
  SessionHistoryResponseSchema,
} from '@monetize/types/app';
import { ENVIRONMENT } from '@monetize/ui/config';
import isArray from 'lodash/isArray';
import { v4 as uuidv4 } from 'uuid';
import { doCreateJWTToken } from '../api/usersService';
import { SUB_AGENT_TAGS } from '../constants/chat';
import { delay } from './chatStream';
import { logger } from './logger';

// Thrown for a response that will never succeed on retry (auth/not-found) --
// distinct from returning null, which callers already treat as "transient,
// keep retrying/polling."
export class TerminalHistoryError extends Error {}
const TERMINAL_STATUSES = new Set([401, 403, 404]);
// Exported so tests can drive fake timers by the exact value, matching how
// RETRY_CHECK_DELAY_MS/MAX_POLL_ATTEMPTS/MAX_STATUS_CHECK_ATTEMPTS already
// are below.
export const HISTORY_FETCH_TIMEOUT_MS = 10000;

export async function fetchSessionHistory(
  sessionId: string,
  tenantId: string,
  userId: string,
  signal?: AbortSignal,
): Promise<SessionHistoryResponse | null> {
  try {
    const token = await doCreateJWTToken('AI_ASSISTANT');
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(
      () => timeoutController.abort(),
      HISTORY_FETCH_TIMEOUT_MS,
    );
    const onCallerAbort = () => timeoutController.abort();
    // `signal` may already be aborted by the time we get here (e.g. it
    // fired during the doCreateJWTToken await above) -- an 'abort' event
    // never replays to a listener added after it fired, so that case must
    // be checked explicitly rather than relying on the listener alone.
    if (signal?.aborted) {
      onCallerAbort();
    } else {
      signal?.addEventListener('abort', onCallerAbort, { once: true });
    }
    try {
      const res = await fetch(
        `${ENVIRONMENT.CHAT_URL}/sessions/${sessionId}/history`,
        {
          method: 'GET',
          headers: {
            'x-tenant-id': tenantId,
            'x-user-id': userId,
            Authorization: `Bearer ${token.token}`,
          },
          signal: timeoutController.signal,
        },
      );
      if (!res.ok) {
        if (TERMINAL_STATUSES.has(res.status)) {
          throw new TerminalHistoryError(`${res.status} ${res.statusText}`);
        }
        return null;
      }
      const parsed = SessionHistoryResponseSchema.safeParse(await res.json());
      return parsed.success ? parsed.data : null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onCallerAbort);
    }
  } catch (err) {
    if (err instanceof TerminalHistoryError) {
      throw err; // propagate -- signals "stop retrying", not "try again"
    }
    logger.warn('chatHistoryFetch: failed to fetch session history', err);
    return null;
  }
}

function extractTextContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }
  if (isArray(content)) {
    return content
      .filter(
        (block): block is { type: string; text: string } =>
          typeof block === 'object' &&
          block !== null &&
          (block as { type?: unknown }).type === 'text' &&
          typeof (block as { text?: unknown }).text === 'string',
      )
      .map((block) => block.text)
      .join('\n\n');
  }
  return '';
}

// Every human message is wrapped by the backend's `user_query()` prompt
// template (ai-assistant `src/ai/prompts/user_query.py`) before being sent
// to the LLM -- "\n        # User Query\n        {query}\n        \n
// {context}\n    ".rstrip() -- and that wrapped text, not the raw query, is
// what's persisted in the LangGraph checkpoint this endpoint reads from.
// The live SSE view never shows this: it renders what the user actually
// typed (chatSocket.tsx's sendMessage adds the message locally, never from
// a server echo), so reconstructing from history needs to invert the same
// template to match, rather than surfacing the internal wrapper verbatim.
// Captures the exact indentation used before {query} (group 1) so the
// context separator below can be matched precisely against that same
// literal indentation, rather than any blank line -- a user's own
// multi-paragraph query is very likely to contain its own blank lines
// (typed with no leading whitespace at all), which must NOT be mistaken
// for the template's own context separator or a multi-paragraph query
// would be silently truncated after its first paragraph.
const USER_QUERY_HEADER = /^\s*#\s*User Query\s*\n([ \t]*)/;

function unwrapUserQuery(text: string): string {
  const headerMatch = text.match(USER_QUERY_HEADER);
  if (!headerMatch) {
    return text; // doesn't match the template -- return as-is, not corrupted
  }
  const [fullMatch, indent] = headerMatch;
  const withoutHeader = text.slice(fullMatch.length);
  if (!indent) {
    // No indentation captured to anchor a precise separator match against
    // -- can't safely distinguish a real context block from the query's
    // own blank lines, so return everything rather than risk truncating a
    // multi-paragraph query.
    return withoutHeader.trim();
  }
  const separator = `\n${indent}\n${indent}`;
  const separatorIndex = withoutHeader.indexOf(separator);
  const query =
    separatorIndex === -1
      ? withoutHeader
      : withoutHeader.slice(0, separatorIndex);
  return query.trim();
}

/**
 * Maps the raw internal LangGraph message list (as returned by the history
 * endpoint) into the frontend's flat, display-ready Message list.
 *
 * This endpoint returns multi-agent handoff scaffolding alongside the real
 * content -- not a pre-filtered "what the user saw" list. The live SSE path
 * already hides this complexity (isSubAgentEvent/SUB_AGENT_TAGS in
 * chatSocket.tsx, with a fallback that promotes sub-agent content back if no
 * supervisor message is visible for a turn); this replicates that same
 * algorithm so a reconstructed conversation matches what was actually shown
 * live, rather than surfacing raw internal handoff chatter or duplicate
 * bubbles for the same answer.
 *
 * A turn whose last AI message is a sub-agent's is one where the sub-agent
 * ended the run without handing back (BP-18709 supervisor bypass; the
 * backend already strips handoff-back messages from this endpoint). Live,
 * the `message` event names that final message as the response, so here the
 * supervisor's own text is kept plus only that last sub-agent message. An
 * older session whose turn had no supervisor text at all also matches this
 * rule and now rebuilds with just the last sub-agent message rather than
 * every one -- fewer than the live view promoted, so the don't-regress
 * checks against countReconstructableMessages keep the local copy instead.
 */
export function mapHistoryMessages(
  rawMessages: ChatHistoryMessage[],
): Message[] {
  const turns: ChatHistoryMessage[][] = [];
  for (const msg of rawMessages) {
    if (msg.type === 'human') {
      turns.push([msg]);
    } else if (msg.type === 'ai' && turns.length > 0) {
      turns[turns.length - 1].push(msg);
    }
    // 'tool'/'system' messages are dropped entirely -- never shown as
    // bubbles live either.
  }

  const now = Date.now();
  const result: Omit<Message, 'timestamp'>[] = [];

  for (const turn of turns) {
    for (const msg of turn) {
      if (msg.type !== 'human') {
        continue;
      }
      const text = unwrapUserQuery(extractTextContent(msg.content));
      if (text) {
        result.push({
          id: msg.id ?? uuidv4(),
          content: text,
          role: 'user',
          type: 'standard',
        });
      }
    }

    const aiMessages = turn
      .filter((msg) => msg.type === 'ai')
      .map((msg) => ({ raw: msg, text: extractTextContent(msg.content) }))
      .filter(({ text }) => text.length > 0);

    const isSubAgent = (msg: ChatHistoryMessage) =>
      !!msg.name && SUB_AGENT_TAGS.includes(msg.name);

    // A sub-agent message that calls a tool is narration ahead of the call,
    // which the live view shows as an activity step, never as a reply. A run
    // stopped mid-call ends on one.
    const isSubAgentAnswer = ({ raw }: { raw: ChatHistoryMessage }) =>
      isSubAgent(raw) && !raw.tool_calls?.length;

    const visible = aiMessages.filter(({ raw }) => !isSubAgent(raw));
    const subAgent = aiMessages.filter(isSubAgentAnswer);
    const lastAiMessage = aiMessages[aiMessages.length - 1];
    let chosen;
    if (lastAiMessage && isSubAgentAnswer(lastAiMessage)) {
      // The sub-agent ended the run itself, so its last message is the
      // response -- the same one the live `message` event names.
      chosen = [...visible, lastAiMessage];
    } else {
      chosen = visible.length > 0 ? visible : subAgent;
    }

    for (const { raw, text } of chosen) {
      result.push({
        id: raw.id ?? uuidv4(),
        content: text,
        role: 'assistant',
        type: 'standard',
      });
    }
  }

  // Backend has no per-message timestamp field at all -- MessageItem.tsx
  // does render `format(new Date(message.timestamp), 'h:mm a')` to the
  // user, so synthesize distinct, index-ordered (not identical) values;
  // purely cosmetic since rendering is plain array order, not
  // timestamp-sorted.
  return result.map((msg, index) => ({
    ...msg,
    timestamp: new Date(now - (result.length - index) * 1000),
  }));
}

// Counts local messages that will also appear in a server-side history
// reconstruction, for a fair "has the server caught up" comparison against
// mapHistoryMessages's output above -- the synthetic hello message, error
// bubbles, and hidden sub-agent messages never appear there, so comparing
// raw array lengths against them would always undercount the server side.
export function countReconstructableMessages(messages: Message[]): number {
  return messages.filter(
    (m) => !m.isHelloMessage && m.type !== 'error' && !m.isSubAgentMessage,
  ).length;
}

// Fixed interval, not exponential -- simpler/more predictable than reusing
// chatStream.ts's reconnectDelayMs growing curve for these two retry
// mechanisms (the pre-existing resend-loop backoff is untouched, unrelated
// prior behavior).
export const RETRY_CHECK_DELAY_MS = 5000;

// A single turn can chain multiple tool round-trips and LLM calls across a
// supervisor/sub-agent handoff (delegate -> sub-agent tool calls -> sub-
// agent's own LLM synthesis -> hand back -> supervisor's own final LLM
// synthesis) -- a confirmed 200 + "running" response is a healthy signal
// the backend is still genuinely working, not a failure, so it must never
// count against a give-up budget on its own (an earlier design that capped
// *all* checks, healthy or not, was observed giving up while the backend
// was still working on exactly this kind of multi-step query). This limit
// applies only to *consecutive* failed fetches (network error, non-2xx,
// unparseable body -- fetchSessionHistory can't currently distinguish
// "transient hiccup" from "session genuinely gone", both return null) --
// any successful check, healthy or not, resets the streak, so occasional
// isolated blips spread across a long poll session never accumulate into a
// false give-up; only a *sustained* run of failures does. Tunable; exported
// so tests can mock the exact number of failures, matching how
// MAX_RECONNECT_ATTEMPTS is already exported from chatStream.ts.
export const MAX_POLL_ATTEMPTS = 100;

export async function pollForTurnCompletion(
  sessionId: string,
  tenantId: string,
  userId: string,
  signal?: AbortSignal,
  // Reports each individual check's outcome as it happens (not just the
  // final result) so the caller can distinguish "confirmed still running"
  // (a healthy 200 -- UI should show a generating/thinking state) from
  // "the status check itself is failing" (UI should show a reconnecting
  // state) instead of treating this whole poll window as one undifferentiated
  // "reconnecting" state throughout.
  onCheckResult?: (failed: boolean) => void,
): Promise<SessionHistoryResponse | null> {
  // Fixed RETRY_CHECK_DELAY_MS between checks, whether the previous check
  // succeeded-but-still-running or failed outright -- waits, then checks
  // (the caller already did one immediate check before entering this
  // loop). Aborts early via `signal` if provided and the user navigates
  // away/cancels or the mount-restoration flow's own controller fires.
  let failedFetches = 0;
  while (failedFetches < MAX_POLL_ATTEMPTS) {
    if (signal?.aborted) {
      return null;
    }
    await delay(RETRY_CHECK_DELAY_MS, signal);
    if (signal?.aborted) {
      return null;
    }
    let history: SessionHistoryResponse | null;
    try {
      history = await fetchSessionHistory(sessionId, tenantId, userId, signal);
    } catch (err) {
      if (err instanceof TerminalHistoryError) {
        return null; // session gone/unauthorized -- stop polling, let the caller fall back
      }
      throw err;
    }
    if (history?.turn_status === 'completed') {
      onCheckResult?.(false);
      return history;
    }
    if (history) {
      // Confirmed still running -- healthy, expected for a long multi-step
      // turn. Keep polling indefinitely; resets the failure streak below
      // (a single isolated blip earlier in a long poll session shouldn't
      // count toward the same budget as a sustained outage).
      onCheckResult?.(false);
      failedFetches = 0;
      continue;
    }
    onCheckResult?.(true);
    failedFetches++;
  }
  // MAX_POLL_ATTEMPTS *consecutive* failed fetches (session genuinely gone,
  // or a sustained outage) -- caller terminates distinctly, does not fall
  // back to resending a prompt.
  return null;
}

// Fewer than MAX_POLL_ATTEMPTS -- this is tolerating a transient blip on a
// single check, not waiting for a slow turn to finish; 2 x 5s = 10s max
// before giving up.
export const MAX_STATUS_CHECK_ATTEMPTS = 3;

export async function fetchSessionHistoryWithRetry(
  sessionId: string,
  tenantId: string,
  userId: string,
  signal?: AbortSignal,
): Promise<SessionHistoryResponse | null> {
  // Used for the initial status check right after a drop is detected, and
  // by the mount-restoration flow, each with their own controller -- signal
  // is optional since not every caller has one to pass. A single failed
  // fetch there (before we even know whether the turn is running or
  // completed) shouldn't immediately force a resend -- that's the more
  // disruptive fallback (starts a new turn) compared to just trying the
  // cheap GET a couple more times first, at a fixed RETRY_CHECK_DELAY_MS
  // interval.
  for (let attempt = 1; attempt <= MAX_STATUS_CHECK_ATTEMPTS; attempt++) {
    if (signal?.aborted) {
      return null;
    }
    let history: SessionHistoryResponse | null;
    try {
      history = await fetchSessionHistory(sessionId, tenantId, userId, signal);
    } catch (err) {
      if (err instanceof TerminalHistoryError) {
        return null; // session gone/unauthorized -- stop retrying, let the caller fall back
      }
      throw err;
    }
    if (history) {
      return history;
    }
    if (attempt === MAX_STATUS_CHECK_ATTEMPTS) {
      return null;
    }
    await delay(RETRY_CHECK_DELAY_MS, signal);
    if (signal?.aborted) {
      return null;
    }
  }
  return null;
}
